/**
 * Client Priced Options Helpers
 * Note: Soon to be superseded by product-configuration.helper (Complex Products)
 */
import { isPresent } from '../../../utils/array/filter.utils';
import { type AddToCartInput, type AddToCartPricedOption, type PricedOptionTypeEnum } from '../../__generated__/graphql-client-types';
import type { ProductPricedOption } from '../../types/configuration.types';
import { type OptionSelection, OptionSelectionType, type Selection } from '../../types/product-option.types';
import { isSelectedOptionValueBased, serializeNestedOption } from './configuration/product-configuration.helper';

export type ProjectInformation = {
	shoppingListCartIds: string;
	id: number;
	name: string;
};

/**
 * Translates Priced Options into Add To Cart priced options input
 */
export const translatePricedOptionsToAddToCart = (pricedOptions?: ProductPricedOption[]): AddToCartPricedOption[] | undefined => {
	return pricedOptions
		? pricedOptions.map((pricedOption) => ({
				pricedOptionId: Number(pricedOption.id),
				pricedOptionText: pricedOption.type === 'TEXTBOX' ? pricedOption.value : null
		  }))
		: undefined;
};

/**
 * Serializes Complex Products new selection into legacy priced options to support some components
 * still using the old experience.
 * Note: This method along with this entire file will be going away.
 */
export const serializeComplexProductsSelectionToPricedOptions = (selection?: Selection): ProductPricedOption[] => {
	const pricedOptionType = (option: OptionSelection): PricedOptionTypeEnum => {
		if (option.type === OptionSelectionType.SINGLE) {
			return 'RADIO';
		} else if (option.type === OptionSelectionType.MULTIPLE) {
			return 'CHECKBOX';
		} else {
			return 'TEXTBOX';
		}
	};
	return selection
		? selection.groups.reduce((pricedOptions: ProductPricedOption[], groupSelection) => {
				return groupSelection.selectionType === 'INLINE' || groupSelection.selectionType === 'COMBINED'
					? pricedOptions.concat(
							groupSelection.options.filter(isSelectedOptionValueBased).map((option) => ({
								id: option.params?.optionId,
								type: pricedOptionType(option),
								value: option.type === OptionSelectionType.INDEPENDENT ? option.value : null
							}))
					  )
					: pricedOptions;
		  }, [])
		: [];
};

/**
 * Serializes Complex Products new selection into sub items for save to project.
 * Note: This method along with this entire file will be going away (same as the previous method).
 */
export const serializeComplexProductsSelectionToSubItems = (selection?: Selection): AddToCartInput[] => {
	return selection
		? selection.groups.reduce((items: AddToCartInput[], groupSelection) => {
				return groupSelection.selectionType === 'NESTED'
					? items.concat(groupSelection.options.map(serializeNestedOption).filter(isPresent))
					: items;
		  }, [])
		: [];
};

type ResolveConfigureUrlParams = {
	path: string;
	variantId: number;
	quantity?: number;
	cartItemIds?: string;
	projectInformation?: ProjectInformation;
};

/**
 * Resolve configure URL with query parameters
 */
export const resolveConfigureUrl = ({ path, variantId, quantity, cartItemIds, projectInformation }: ResolveConfigureUrlParams): string => {
	// the URL constructor needs a valid base URL / host
	// TODO: if we can figure out a way to get the current host we could use appropriate absolute urls
	const parsedURL = new URL(path, 'https://www.build.com');
	parsedURL.searchParams.append('uid', variantId.toString());

	if (quantity) {
		parsedURL.searchParams.append('quantity', quantity.toString());
	}
	if (cartItemIds) {
		parsedURL.searchParams.append('cartItemIds', cartItemIds);
	}
	if (projectInformation) {
		const { shoppingListCartIds, id, name } = projectInformation;
		parsedURL.searchParams.append('shoppingListIds', shoppingListCartIds);
		parsedURL.searchParams.append('project', `${name}:${id}`);
	}
	return `${parsedURL.pathname}${parsedURL.search}`;
};
